import { Fragment, memo, useMemo } from 'react';
import {
  FilterState,
  useProductsFilter,
} from '../../../../../Context/ProductsFilterContext';
import axios from 'axios';
import { IVtsExhibitor } from '../../../Main/Admin/Exhibitors/Exhibitors';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import ProductSizeView from '../../../../../Abstracts/Products/view/ProductSizeView/ProductSizeView';
import { ProductSizeInterface } from '../../../../../../Interface/ProductSize';
import ProductToolbar from '../../../../../Abstracts/Products/ProductToolbar/ProductToolbar';
import useViewports from '../../../../../Hooks/useViewports';
import { ProductAttributeInterface } from '../../../../../../Interface/ProductAttribute';
import ProductAttributeView from '../../../../../Abstracts/Products/view/ProductAttributeView/ProductAttributeView';
import AddToBasketBar from '../../../../../Abstracts/ProductList/AddToBasketBar/AddToBasketBar';
import LoadingSkeleton from '../../../../../Abstracts/ProductList/Products/LoadingSkeleton/LoadingSkeleton';
import { Col, Row } from 'react-bootstrap';
import { useUser } from '../../../../../Context/UserContext';
import { useVts } from '../../../../../Context/VtsContext';
import { formatVtsDayAndMonth } from '../../../../../Helper/formatter';
import SortByFilter from '../../../../../Abstracts/Products/FilterList/Filters/SortByFilter/SortByFilter';
import styles from './VtsProducts.module.scss?module';
import {
  applyVtsOverridesToProductSizes,
  applyVtsOverridesToProductAttributes,
  buildErpIdToIdMap,
} from './utils/applyVtsInfoOverrides';
import VtsProductsProvider from './VtsProductsProvider';

type VtsProductsProps = {
  exhibitor: IVtsExhibitor;
};

type VtsProductsContentProps = {} & VtsProductsProps;

const VtsProductsHeader = memo(() => {
  const { user } = useUser();
  const { vts } = useVts();
  const vtsOrderYear =
    vts?.order_start && new Date(vts?.order_start).getFullYear();
  const vtsConfirmationYear =
    vts?.order_start && new Date(vts?.confirmation_start).getFullYear();

  return (
    <Row>
      <Col>
        <h2 className={'popupHeadline m-0'}>Place your order</h2>
        {user && !user.is_exhibitor && (
          <p className={'text-muted text-center m-0'}>
            Place your order here between{' '}
            <b>{vts?.order_start && formatVtsDayAndMonth(vts?.order_start)}</b>{' '}
            and <b> {vts?.order_end && formatVtsDayAndMonth(vts?.order_end)}</b>
            , <b>{vtsOrderYear}</b>.
            <br />
            Adjust and confirm your order in your account details at
            www.naskorsports.com between{' '}
            {vts?.confirmation_start && (
              <b>{formatVtsDayAndMonth(vts?.confirmation_start)}</b>
            )}{' '}
            and{' '}
            {vts?.confirmation_end && (
              <b>{formatVtsDayAndMonth(vts?.confirmation_end)}</b>
            )}
            , <b>{vtsConfirmationYear}</b>.
            <br />
            Flexible delivery feature will be available in your account details.
          </p>
        )}
      </Col>
    </Row>
  );
});

const VtsProductsContent = ({ exhibitor }: VtsProductsContentProps) => {
  const { filters } = useProductsFilter();
  const { xs, sm, md } = useViewports();
  const isMobile = xs || sm || md;

  async function fetchProductSizeList() {
    const url = `/api/onlineshop/product-sizes?context[]=vts&context[]=nutrition_facts&context[]=product_size_list&context[]=sell_sheets&context[]=product_attributes&exhibitor_id=${exhibitor.id}&sortBy=${filters.sortBy}`;
    const res = await axios.get(url);
    return res.data;
  }

  async function fetchProductAttributeList() {
    const url = `/api/onlineshop/product-attributes?context[]=vts&context[]=nutrition_facts&context[]=product_size_list&context[]=product_size&exhibitor_id=${exhibitor.id}&sortBy=${filters.sortBy}`;
    const res = await axios.get(url);
    return res.data;
  }

  const productSizeQuery = useQuery<ProductSizeInterface[]>({
    queryKey: ['product-size-list', exhibitor.id, filters.sortBy],
    queryFn: fetchProductSizeList,
    enabled: filters.view === 'grid',
    gcTime: 0,
  });

  const productAttributeQuery = useQuery<ProductAttributeInterface[]>({
    queryKey: ['product-attribute-list', exhibitor.id, filters.sortBy],
    queryFn: fetchProductAttributeList,
    enabled: filters.view === 'list' || filters.view === 'table',
    gcTime: 0,
  });

  const activeQuery =
    filters.view === 'grid' ? productSizeQuery : productAttributeQuery;

  const transformedProductSizes = useMemo(
    () => applyVtsOverridesToProductSizes(productSizeQuery.data ?? []),
    [productSizeQuery.data]
  );

  const transformedProductAttributes = useMemo(
    () =>
      applyVtsOverridesToProductAttributes(productAttributeQuery.data ?? []),
    [productAttributeQuery.data]
  );

  const erpIdToIdMap = useMemo(
    () =>
      buildErpIdToIdMap(
        productSizeQuery.data ?? [],
        productAttributeQuery.data ?? []
      ),
    [productSizeQuery.data, productAttributeQuery.data]
  );

  return (
    <VtsProductsProvider erpIdToIdMap={erpIdToIdMap}>
      <VtsProductsContentInner
        exhibitor={exhibitor}
        isMobile={isMobile}
        activeQuery={activeQuery}
        filters={filters}
        transformedProductSizes={transformedProductSizes}
        transformedProductAttributes={transformedProductAttributes}
      />
    </VtsProductsProvider>
  );
};

const VtsProductsContentInner = ({
  exhibitor,
  isMobile,
  activeQuery,
  filters,
  transformedProductSizes,
  transformedProductAttributes,
}: {
  exhibitor: IVtsExhibitor;
  isMobile: boolean;
  activeQuery: UseQueryResult<
    ProductSizeInterface[] | ProductAttributeInterface[],
    Error
  >;
  filters: FilterState;
  transformedProductSizes: ProductSizeInterface[];
  transformedProductAttributes: ProductAttributeInterface[];
}) => {
  return (
    <div className="pt-5 pt-xl-3">
      {isMobile ? (
        <div className={styles.SortByFilterMobile}>
          <SortByFilter />
        </div>
      ) : null}

      <div className="pt-4">
        <ProductToolbar
          isMobile={isMobile}
          additionalFilters={[
            { key: 'exhibitor_id', value: exhibitor.id.toString() },
          ]}
        />
      </div>

      {activeQuery?.data?.length > 0 && !activeQuery.isFetching ? (
        <>
          {filters.view === 'grid' ? (
            <ProductSizeView
              data={transformedProductSizes}
              allowBackorder
            />
          ) : null}

          {filters.view === 'list' || filters.view === 'table' ? (
            <ProductAttributeView
              data={transformedProductAttributes}
              view={filters.view}
              allowBackorder
            />
          ) : null}
        </>
      ) : null}

      {activeQuery.isFetching ||
      activeQuery.isRefetching ||
      activeQuery.isPending ? (
        <>
          {[...Array(5)].map((_, key) => (
            <Fragment key={key}>
              <LoadingSkeleton />
            </Fragment>
          ))}
        </>
      ) : null}

      <AddToBasketBar />
    </div>
  );
};

const VtsProducts = memo(({ exhibitor }: VtsProductsProps) => {
  return (
    <>
      <VtsProductsHeader />
      <VtsProductsContent exhibitor={exhibitor} />
    </>
  );
});

export default VtsProducts;
